import type { FunnelFacts, PersonFunnel } from './funnel'

/**
 * The To do list: what needs a person to act, and how soon.
 *
 * Built on the funnel, so an issue clears itself the moment the event that
 * resolves it is recorded; nobody closes anything by hand. Priority says who
 * is waiting on whom:
 *
 *   1 Today:     a paying customer is blocked by us, or money is at risk.
 *   2 This week: the customer needs a nudge from us.
 *   3 Watch:     the customer is slow; the automatic emails should cover it.
 *
 * Thresholds are in days and are first guesses, like the funnel's.
 */

export type IssuePriority = 1 | 2 | 3
/** Where the fix happens: the person's page, or their order's page. */
export type IssueAction = 'person' | 'order'

/** Days a status may last before it becomes a To do item. */
export const TODO_DAYS = {
  approvalWaiting: 2,
  notShipped: 3,
  wroteNoFile: 3,
  notPickedUp: 2,
  notStarted: 3,
  noFile: 7,
} as const

export const PRIORITIES = [
  { priority: 1, title: 'Today', why: 'We are blocking a paying customer, or money is at risk.' },
  { priority: 2, title: 'This week', why: 'The customer needs a nudge from us.' },
  { priority: 3, title: 'Watch', why: 'The customer is slow; the automatic emails should handle it.' },
] as const satisfies ReadonlyArray<{ priority: IssuePriority; title: string; why: string }>

/* `rule` is the threshold in words, built from TODO_DAYS, so the legend on the
   To do page can never drift from what issuesFor does. */
export const ISSUE_TYPES = [
  { id: 'carrier_problem', priority: 1, label: 'Kit: carrier problem or returned', action: 'order', rule: 'The carrier reports a problem, or the kit came back' },
  { id: 'refund_failed', priority: 1, label: 'Refund failed', action: 'order', rule: 'Wix reports the refund failed; shown even after someone leaves the funnel' },
  { id: 'ready_to_activate', priority: 1, label: 'Kit delivered and approved, still a demo account', action: 'person', rule: 'Kit delivered and eligibility approved, but the app account is still a demo' },
  { id: 'approval_waiting', priority: 1, label: 'Prescription graded, no approval decision', action: 'person', rule: `Graded more than ${TODO_DAYS.approvalWaiting} days ago, with no approval decision` },
  { id: 'not_shipped', priority: 1, label: 'Paid, kit not shipped', action: 'order', rule: `Paid more than ${TODO_DAYS.notShipped} days ago, no shipping label yet` },
  { id: 'bad_file', priority: 2, label: 'File sent is not a readable prescription', action: 'person', rule: 'The file was not a prescription, or could not be read: ask again' },
  { id: 'no_date_of_birth', priority: 2, label: 'Prescription read, date of birth missing', action: 'person', rule: 'The prescription was read but shows no date of birth, and none is entered' },
  { id: 'wrote_no_file', priority: 2, label: 'Wrote to us without a file', action: 'person', rule: `Wrote to us ${TODO_DAYS.wroteNoFile} or more days ago without attaching a file` },
  { id: 'not_picked_up', priority: 2, label: 'Label created, carrier has not picked it up', action: 'order', rule: `Label created more than ${TODO_DAYS.notPickedUp} days ago, no carrier scan since` },
  { id: 'not_started', priority: 2, label: 'Account active, training not started', action: 'person', rule: `A real account, no session more than ${TODO_DAYS.notStarted} days after delivery and approval` },
  { id: 'no_file', priority: 3, label: 'No prescription since the welcome email', action: 'person', rule: `No file ${TODO_DAYS.noFile} or more days after the welcome email` },
  { id: 'unreachable', priority: 3, label: 'Email bounced or unsubscribed', action: 'person', rule: 'An email to them bounced, or they unsubscribed' },
  { id: 'at_risk', priority: 3, label: 'Training stalled (at risk)', action: 'person', rule: 'The training stage is At risk: no session for 8 to 21 days' },
] as const satisfies ReadonlyArray<{ id: string; priority: IssuePriority; label: string; action: IssueAction; rule: string }>

export type IssueId = (typeof ISSUE_TYPES)[number]['id']

export interface Issue {
  id: IssueId
  priority: IssuePriority
  label: string
  action: IssueAction
  /** When the wait began; null when no event dates it. */
  since: string | null
}


const DAY = 86_400_000
const daysSince = (iso: string | null, now: Date) => (iso ? (now.getTime() - new Date(iso).getTime()) / DAY : null)
const later = (...xs: Array<string | null>) => xs.filter((x): x is string => !!x).sort().pop() ?? null

export function issuesFor(
  p: PersonFunnel,
  facts: FunnelFacts,
  stage: string | null,
  now: Date = new Date(),
): Issue[] {
  const out: Issue[] = []
  const add = (id: IssueId, since: string | null) => {
    const t = ISSUE_TYPES.find((x) => x.id === id)!
    out.push({ id, priority: t.priority, label: t.label, action: t.action, since })
  }
  const over = (iso: string | null, days: number) => {
    const d = daysSince(iso, now)
    return d != null && d > days
  }
  const atLeast = (iso: string | null, days: number) => {
    const d = daysSince(iso, now)
    return d != null && d >= days
  }
  const s = p.steps

  if (p.refundFailed) add('refund_failed', facts.refundedAt ?? facts.paidAt)
  /* Someone refunded or ruled ineligible has left; only a failed refund is still ours. */
  if (p.exit) return out

  /* Priority 1 */
  if (s.delivered.status === 'problem' || s.delivered.status === 'returned') add('carrier_problem', s.delivered.since)
  if (s.activated.status === 'waiting') add('ready_to_activate', s.activated.since)
  if (s.eligible.status === 'graded' && over(facts.gradedAt, TODO_DAYS.approvalWaiting)) add('approval_waiting', facts.gradedAt)
  if (s.shipped.status === 'not_shipped' && over(facts.paidAt, TODO_DAYS.notShipped)) add('not_shipped', facts.paidAt)

  /* Priority 2 */
  if (s.read.status === 'not_prescription' || s.read.status === 'unreadable') add('bad_file', s.read.since)
  if (s.read.status === 'no_date_of_birth') add('no_date_of_birth', s.read.since)
  if (s.sent.status === 'wrote_no_file' && atLeast(facts.wroteAt, TODO_DAYS.wroteNoFile)) add('wrote_no_file', facts.wroteAt)
  if (s.shipped.status === 'label_created' && over(s.shipped.since, TODO_DAYS.notPickedUp)) add('not_picked_up', s.shipped.since)
  /* A real account with no session yet. The account's change from Demo is not
     dated, so the wait counts from the later of delivery and approval, the two
     things activation follows; failing those, from payment. */
  if (s.started.status === 'not_started') {
    const from = later(s.delivered.done ? s.delivered.since : null, s.eligible.done ? s.eligible.since : null) ?? facts.paidAt
    if (over(from, TODO_DAYS.notStarted)) add('not_started', from)
  }

  /* Priority 3 */
  if (s.sent.status === 'nothing' && facts.askedAt && atLeast(facts.askedAt, TODO_DAYS.noFile)) add('no_file', facts.askedAt)
  if (facts.unreachable) add('unreachable', null)
  if (stage === 'at_risk') add('at_risk', null)

  return out
}
